import React, { useState, useEffect, useCallback, useRef } from 'react';
import './FamilyTree.css';

const RELATIONSHIP_TYPES = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'grandmother', label: 'Grandmother' },
  { value: 'grandfather', label: 'Grandfather' },
  { value: 'grandson', label: 'Grandson' },
  { value: 'granddaughter', label: 'Granddaughter' },
  { value: 'aunt', label: 'Aunt' },
  { value: 'uncle', label: 'Uncle' },
  { value: 'cousin', label: 'Cousin' },
  { value: 'niece', label: 'Niece' },
  { value: 'nephew', label: 'Nephew' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' },
];

const CALL_PURPOSES = [
  { value: 'check_in', label: 'Check In', icon: '💬', desc: 'See how they\'re doing' },
  { value: 'share_memory', label: 'Share Memory', icon: '💭', desc: 'Share a story about you' },
  { value: 'birthday', label: 'Birthday', icon: '🎂', desc: 'Wish happy birthday' },
  { value: 'thinking_of_you', label: 'Thinking of You', icon: '💝', desc: 'You\'ve been on their mind' },
  { value: 'custom', label: 'Custom', icon: '✨', desc: 'General call' },
];

const GENERATION_MAP = {
  grandfather: -2, grandmother: -2,
  father: -1, mother: -1, aunt: -1, uncle: -1,
  brother: 0, sister: 0, spouse: 0, cousin: 0, friend: 0, other: 0,
  son: 1, daughter: 1, niece: 1, nephew: 1,
  grandson: 2, granddaughter: 2,
};

const RELATIONSHIP_ICONS = {
  spouse: '💍', mother: '👩', father: '👨', son: '👦', daughter: '👧',
  brother: '🧑', sister: '👩', grandmother: '👵', grandfather: '👴',
  grandson: '👶', granddaughter: '👶', aunt: '👩', uncle: '🧑',
  cousin: '🧑', niece: '👧', nephew: '👦', friend: '🤝', other: '👤',
};

function FamilyTree({ isOpen, onClose, token, api }) {
  const [members, setMembers] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberMemories, setMemberMemories] = useState([]);
  const [loadingMemories, setLoadingMemories] = useState(false);
  const [toast, setToast] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCallMenu, setShowCallMenu] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '', recurring: '', purpose: 'check_in' });
  const [scheduling, setScheduling] = useState(false);
  const treeRef = useRef(null);

  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchTree = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`${api}/family/tree`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load family tree');
      const data = await res.json();
      setMembers(data.members || []);
      setEdges(data.edges || []);
    } catch (err) {
      console.error('Family tree fetch error:', err);
      showToast('Failed to load family tree', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, api, showToast]);

  const fetchSuggestions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${api}/family/suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch (err) {
      console.error('Suggestions fetch error:', err);
    }
  }, [token, api]);

  useEffect(() => {
    if (isOpen) {
      fetchTree();
      fetchSuggestions();
    }
  }, [isOpen, fetchTree, fetchSuggestions]);

  const handleDiscover = useCallback(async () => {
    if (!token) return;
    try {
      setDiscovering(true);
      showToast('Scanning memories for family members...', 'info');
      await fetch(`${api}/family/discover`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Poll for results
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        await fetchSuggestions();
        if (attempts >= 15) {
          clearInterval(poll);
          setDiscovering(false);
          setShowSuggestions(true);
          showToast('Discovery complete!', 'info');
        }
      }, 3000);
      // Stop after first result or timeout
      setTimeout(() => {
        clearInterval(poll);
        setDiscovering(false);
        setShowSuggestions(true);
        fetchSuggestions();
      }, 20000);
    } catch (err) {
      showToast('Discovery failed', 'error');
      setDiscovering(false);
    }
  }, [token, api, showToast, fetchSuggestions]);

  const handleAcceptSuggestion = useCallback(async (suggestion) => {
    try {
      const res = await fetch(`${api}/family/suggestions/${suggestion.id}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationship_to_user: suggestion.relationship_guess }),
      });
      if (!res.ok) throw new Error('Failed to accept');
      showToast(`${suggestion.name} added to family tree!`, 'info');
      fetchTree();
      fetchSuggestions();
    } catch (err) {
      showToast('Failed to accept suggestion', 'error');
    }
  }, [token, api, showToast, fetchTree, fetchSuggestions]);

  const handleDismissSuggestion = useCallback(async (suggestionId) => {
    try {
      await fetch(`${api}/family/suggestions/${suggestionId}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSuggestions();
    } catch (err) {
      console.error('Dismiss error:', err);
    }
  }, [token, api, fetchSuggestions]);

  const fetchMemberMemories = useCallback(async (memberId) => {
    if (!token) return;
    try {
      setLoadingMemories(true);
      const res = await fetch(`${api}/family/members/${memberId}/memories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load memories');
      const data = await res.json();
      setMemberMemories(data.memories || []);
    } catch (err) {
      console.error('Member memories fetch error:', err);
      setMemberMemories([]);
    } finally {
      setLoadingMemories(false);
    }
  }, [token, api]);

  const handleSelectMember = useCallback((member) => {
    setSelectedMember(member);
    setShowCallMenu(false);
    setShowScheduleForm(false);
    fetchMemberMemories(member.id);
  }, [fetchMemberMemories]);

  const handleDeleteMember = useCallback(async (memberId) => {
    if (!window.confirm('Remove this family member? This cannot be undone.')) return;
    try {
      const res = await fetch(`${api}/family/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      showToast('Family member removed', 'info');
      setSelectedMember(null);
      fetchTree();
    } catch (err) {
      showToast('Failed to remove member', 'error');
    }
  }, [token, api, fetchTree, showToast]);

  const handleCallMember = useCallback(async (memberId, memberName, callPurpose = 'check_in') => {
    const purposeLabel = CALL_PURPOSES.find(p => p.value === callPurpose)?.label || 'Check In';
    if (!window.confirm(`Call ${memberName}? (${purposeLabel})`)) return;
    try {
      const res = await fetch(`${api}/family/call/${memberId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_purpose: callPurpose }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to call');
      }
      showToast(`Calling ${memberName}...`, 'info');
      setShowCallMenu(false);
    } catch (err) {
      showToast(err.message, 'error');
    }
  }, [token, api, showToast]);

  const handleScheduleCall = useCallback(async (memberId) => {
    if (!scheduleForm.date || !scheduleForm.time) {
      showToast('Please select a date and time', 'error');
      return;
    }
    try {
      setScheduling(true);
      const scheduledAt = `${scheduleForm.date}T${scheduleForm.time}:00`;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
      const res = await fetch(`${api}/family/schedule-call/${memberId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduled_at: scheduledAt,
          timezone: tz,
          recurring: scheduleForm.recurring || null,
          call_purpose: scheduleForm.purpose,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to schedule');
      }
      const data = await res.json();
      showToast(data.message || 'Call scheduled!', 'info');
      setShowScheduleForm(false);
      setScheduleForm({ date: '', time: '', recurring: '', purpose: 'check_in' });
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setScheduling(false);
    }
  }, [token, api, showToast, scheduleForm]);

  // ── Group members by generation for the tree layout ──
  const generationGroups = {};
  members.forEach((m) => {
    const gen = GENERATION_MAP[m.relationship_to_user] ?? 0;
    if (!generationGroups[gen]) generationGroups[gen] = [];
    generationGroups[gen].push(m);
  });
  const sortedGens = Object.keys(generationGroups).map(Number).sort((a, b) => a - b);

  if (!isOpen) return null;

  return (
    <div className="ft-overlay">
      <div className="ft-container">
        {/* Header */}
        <div className="ft-header">
          <div className="ft-header-text">
            <h2 className="ft-title">Family Tree</h2>
            <p className="ft-subtitle">
              {members.length} {members.length === 1 ? 'member' : 'members'} in your family
              {suggestions.length > 0 && (
                <span className="ft-suggestion-badge" onClick={() => setShowSuggestions(!showSuggestions)}>
                  {suggestions.length} suggested
                </span>
              )}
            </p>
          </div>
          <div className="ft-header-actions">
            <button
              className="ft-discover-btn"
              onClick={handleDiscover}
              disabled={discovering}
            >
              {discovering ? '🔍 Scanning...' : '🔍 Discover'}
            </button>
            <button className="ft-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Suggestions Banner */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="ft-suggestions-bar">
            <div className="ft-suggestions-header">
              <span>🔍 Discovered from your memories</span>
              <button className="ft-suggestions-close" onClick={() => setShowSuggestions(false)}>Hide</button>
            </div>
            <div className="ft-suggestions-list">
              {suggestions.map((s) => (
                <div key={s.id} className="ft-suggestion-card">
                  <div className="ft-suggestion-info">
                    <span className="ft-suggestion-name">{s.name}</span>
                    <span className="ft-suggestion-rel">{s.relationship_guess}</span>
                    {s.source_snippets?.[0] && (
                      <span className="ft-suggestion-snippet">"{s.source_snippets[0]}"</span>
                    )}
                    <span className="ft-suggestion-conf">{Math.round((s.confidence || 0) * 100)}% confident</span>
                  </div>
                  <div className="ft-suggestion-actions">
                    <button className="ft-sug-accept" onClick={() => handleAcceptSuggestion(s)}>✓ Add</button>
                    <button className="ft-sug-dismiss" onClick={() => handleDismissSuggestion(s.id)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="ft-body">
          {/* Left: Tree */}
          <div className="ft-tree-panel" ref={treeRef}>
            {loading ? (
              <div className="ft-loading">Loading family tree...</div>
            ) : members.length === 0 ? (
              <div className="ft-empty">
                <div className="ft-empty-icon">👨‍👩‍👧‍👦</div>
                <h3>Start Building Your Family Tree</h3>
                <p>Add family members to see how your memories connect to the people in your life.</p>
                <button className="ft-add-btn-large" onClick={() => { setEditingMember(null); setShowAddModal(true); }}>
                  + Add First Family Member
                </button>
              </div>
            ) : (
              <>
                {/* The You node at center */}
                <div className="ft-tree">
                  {sortedGens.map((gen) => (
                    <div key={gen} className="ft-generation" data-gen={gen}>
                      <div className="ft-gen-label">
                        {gen === -2 && 'Grandparents'}
                        {gen === -1 && 'Parents'}
                        {gen === 0 && 'Your Generation'}
                        {gen === 1 && 'Children'}
                        {gen === 2 && 'Grandchildren'}
                      </div>
                      <div className="ft-gen-row">
                        {gen === 0 && (
                          <div className="ft-node ft-node-self">
                            <div className="ft-node-icon">🧬</div>
                            <div className="ft-node-name">You</div>
                          </div>
                        )}
                        {generationGroups[gen].map((m) => (
                          <div
                            key={m.id}
                            className={`ft-node ${selectedMember?.id === m.id ? 'ft-node-selected' : ''}`}
                            onClick={() => handleSelectMember(m)}
                          >
                            <div className="ft-node-icon">
                              {RELATIONSHIP_ICONS[m.relationship_to_user] || '👤'}
                            </div>
                            <div className="ft-node-name">{m.name}</div>
                            <div className="ft-node-rel">{m.relationship_label || m.relationship_to_user}</div>
                            {m.memory_count > 0 && (
                              <div className="ft-node-memories">{m.memory_count} memories</div>
                            )}
                            {m.phone_approved && m.phone_number && (
                              <div className="ft-node-phone">📞</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="ft-add-btn-float"
                  onClick={() => { setEditingMember(null); setShowAddModal(true); }}
                >
                  + Add Family Member
                </button>
              </>
            )}
          </div>

          {/* Right: Detail panel */}
          {selectedMember && (
            <div className="ft-detail-panel">
              <div className="ft-detail-header">
                <div className="ft-detail-icon">
                  {RELATIONSHIP_ICONS[selectedMember.relationship_to_user] || '👤'}
                </div>
                <div>
                  <h3 className="ft-detail-name">{selectedMember.name}</h3>
                  <span className="ft-detail-rel">
                    {selectedMember.relationship_label || selectedMember.relationship_to_user}
                  </span>
                </div>
              </div>

              {selectedMember.notes && (
                <p className="ft-detail-notes">{selectedMember.notes}</p>
              )}

              {selectedMember.phone_number && (
                <div className="ft-detail-phone">
                  <span>📱 {selectedMember.phone_number}</span>
                  {selectedMember.phone_approved ? (
                    <span className="ft-phone-approved">Approved for calls</span>
                  ) : (
                    <span className="ft-phone-pending">Not approved</span>
                  )}
                </div>
              )}

              {selectedMember.birth_year && (
                <div className="ft-detail-birth">Born: {selectedMember.birth_year}</div>
              )}

              <div className="ft-detail-actions">
                <button
                  className="ft-action-btn ft-action-edit"
                  onClick={() => { setEditingMember(selectedMember); setShowAddModal(true); }}
                >
                  ✎ Edit
                </button>
                {selectedMember.phone_approved && selectedMember.phone_number && (
                  <>
                    <button
                      className="ft-action-btn ft-action-call"
                      onClick={() => setShowCallMenu(!showCallMenu)}
                    >
                      📞 Call
                    </button>
                    <button
                      className="ft-action-btn ft-action-schedule"
                      onClick={() => { setShowScheduleForm(!showScheduleForm); setShowCallMenu(false); }}
                    >
                      ⏰ Schedule
                    </button>
                  </>
                )}
                <button
                  className="ft-action-btn ft-action-delete"
                  onClick={() => handleDeleteMember(selectedMember.id)}
                >
                  🗑 Remove
                </button>
              </div>

              {/* Call Purpose Menu */}
              {showCallMenu && selectedMember.phone_approved && (
                <div className="ft-call-menu">
                  <div className="ft-call-menu-title">Why is Legacy calling?</div>
                  {CALL_PURPOSES.map((p) => (
                    <button
                      key={p.value}
                      className="ft-call-purpose-btn"
                      onClick={() => handleCallMember(selectedMember.id, selectedMember.name, p.value)}
                    >
                      <span className="ft-purpose-icon">{p.icon}</span>
                      <span className="ft-purpose-info">
                        <span className="ft-purpose-label">{p.label}</span>
                        <span className="ft-purpose-desc">{p.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Schedule Call Form */}
              {showScheduleForm && selectedMember.phone_approved && (
                <div className="ft-schedule-form">
                  <div className="ft-schedule-title">Schedule a call to {selectedMember.name}</div>
                  <label className="ft-field">
                    <span>Purpose</span>
                    <select
                      value={scheduleForm.purpose}
                      onChange={(e) => setScheduleForm(f => ({ ...f, purpose: e.target.value }))}
                    >
                      {CALL_PURPOSES.map((p) => (
                        <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
                      ))}
                    </select>
                  </label>
                  <div className="ft-schedule-row">
                    <label className="ft-field">
                      <span>Date</span>
                      <input
                        type="date"
                        value={scheduleForm.date}
                        onChange={(e) => setScheduleForm(f => ({ ...f, date: e.target.value }))}
                      />
                    </label>
                    <label className="ft-field">
                      <span>Time</span>
                      <input
                        type="time"
                        value={scheduleForm.time}
                        onChange={(e) => setScheduleForm(f => ({ ...f, time: e.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="ft-field">
                    <span>Recurring</span>
                    <select
                      value={scheduleForm.recurring}
                      onChange={(e) => setScheduleForm(f => ({ ...f, recurring: e.target.value }))}
                    >
                      <option value="">One-time</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="weekdays">Weekdays</option>
                    </select>
                  </label>
                  <div className="ft-schedule-actions">
                    <button className="ft-schedule-cancel" onClick={() => setShowScheduleForm(false)}>Cancel</button>
                    <button
                      className="ft-schedule-save"
                      onClick={() => handleScheduleCall(selectedMember.id)}
                      disabled={scheduling}
                    >
                      {scheduling ? 'Scheduling...' : 'Schedule Call'}
                    </button>
                  </div>
                </div>
              )}

              {/* Shared Memories */}
              <div className="ft-detail-memories">
                <h4>Shared Memories ({selectedMember.memory_count || 0})</h4>
                {loadingMemories ? (
                  <div className="ft-loading-small">Loading...</div>
                ) : memberMemories.length === 0 ? (
                  <p className="ft-no-memories">No memories tagged with {selectedMember.name} yet.</p>
                ) : (
                  <div className="ft-memory-list">
                    {memberMemories.slice(0, 20).map((mem) => (
                      <div key={mem.id} className="ft-memory-item">
                        <div className="ft-memory-text">{mem.content?.substring(0, 120)}...</div>
                        {mem.created_at && (
                          <div className="ft-memory-date">
                            {new Date(mem.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <AddMemberModal
            member={editingMember}
            token={token}
            api={api}
            onClose={() => { setShowAddModal(false); setEditingMember(null); }}
            onSaved={() => {
              setShowAddModal(false);
              setEditingMember(null);
              fetchTree();
              showToast(editingMember ? 'Member updated' : 'Member added', 'info');
            }}
            showToast={showToast}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className={`ft-toast ft-toast-${toast.type}`}>{toast.msg}</div>
        )}
      </div>
    </div>
  );
}

// ── Add/Edit Member Modal ──────────────────────────────────────────────────
function AddMemberModal({ member, token, api, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    name: member?.name || '',
    relationship_to_user: member?.relationship_to_user || 'spouse',
    relationship_label: member?.relationship_label || '',
    phone_number: member?.phone_number || '',
    phone_approved: member?.phone_approved || false,
    birth_year: member?.birth_year || '',
    notes: member?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    try {
      setSaving(true);
      const body = {
        ...form,
        birth_year: form.birth_year ? parseInt(form.birth_year, 10) : null,
      };
      const url = member
        ? `${api}/family/members/${member.id}`
        : `${api}/family/members`;
      const res = await fetch(url, {
        method: member ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ft-modal-overlay" onClick={onClose}>
      <div className="ft-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{member ? 'Edit Family Member' : 'Add Family Member'}</h3>

        <label className="ft-field">
          <span>Name *</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g. Nicole"
            autoFocus
          />
        </label>

        <label className="ft-field">
          <span>Relationship</span>
          <select
            value={form.relationship_to_user}
            onChange={(e) => handleChange('relationship_to_user', e.target.value)}
          >
            {RELATIONSHIP_TYPES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </label>

        <label className="ft-field">
          <span>Label (optional)</span>
          <input
            type="text"
            value={form.relationship_label}
            onChange={(e) => handleChange('relationship_label', e.target.value)}
            placeholder="e.g. My rock, Big Sis"
          />
        </label>

        <label className="ft-field">
          <span>Phone Number</span>
          <input
            type="tel"
            value={form.phone_number}
            onChange={(e) => handleChange('phone_number', e.target.value)}
            placeholder="+1 555 123 4567"
          />
        </label>

        {form.phone_number && (
          <label className="ft-field ft-field-row">
            <input
              type="checkbox"
              checked={form.phone_approved}
              onChange={(e) => handleChange('phone_approved', e.target.checked)}
            />
            <span>Approve for outbound calls (the AI can call this number)</span>
          </label>
        )}

        <label className="ft-field">
          <span>Birth Year</span>
          <input
            type="number"
            value={form.birth_year}
            onChange={(e) => handleChange('birth_year', e.target.value)}
            placeholder="1990"
            min="1900"
            max="2030"
          />
        </label>

        <label className="ft-field">
          <span>Notes</span>
          <textarea
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Any notes about this person..."
            rows={3}
          />
        </label>

        <div className="ft-modal-actions">
          <button className="ft-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="ft-modal-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : member ? 'Update' : 'Add Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FamilyTree;
